/**
 * PATH: packages/ui/src/kanban/components/board-view/kanbanCardFaceChrome.ts
 * FILE_ID: UI-KANBAN-CARD-FACE-CHROME-001
 * PURPOSE: Gate tile rail / strip / extras from the persisted face recipe.
 * ROLE IN ARCHITECTURE: Pure I→O between resolveCardFacePrefs and KanbanCard.
 * MAIN EXPORTS: applyCardFaceRail, applyCardFaceStrip, applyCardFaceExtras
 * NON-RESPONSIBILITIES: Does not persist. Hide omits chrome, not card data.
 * NOTES FOR FUTURE AI: Off omits handlers. tileOpen expand never disables host open.
 *   Compose sits on exactly one surface. Allocator/due leave the menu when on rail.
 */
import type { KanbanCardQuickActionHandlers } from '../KanbanCard.types'
import type { ResolvedCardFacePrefs } from './cardFacePrefs'
import { menuShows, railShows } from './cardFaceRecipe'

export function applyCardFaceRail(
  face: ResolvedCardFacePrefs,
  quickActions: KanbanCardQuickActionHandlers | undefined,
): KanbanCardQuickActionHandlers | undefined {
  if (!quickActions) return undefined
  if (face.railMode === 'hidden') {
    return {
      ...quickActions,
      onSetPriority: undefined,
      onSetDueDate: undefined,
      onAssign: undefined,
      onMoveTo: undefined,
      onToggleArchive: undefined,
      onOpenEntries: undefined,
      onAllocateToLane: undefined,
      onAllocateToBoard: undefined,
      onLoadBoards: undefined,
      onLoadBoardLanes: undefined,
      onMoveToBoard: undefined,
      onComposeEmail: undefined,
      onSetActionDate: undefined,
    }
  }
  return {
    ...quickActions,
    onSetPriority: railShows(face.place, 'priority')
      ? quickActions.onSetPriority
      : undefined,
    onSetDueDate:
      face.fields.due && railShows(face.place, 'due')
        ? quickActions.onSetDueDate
        : undefined,
    onAssign: railShows(face.place, 'assign') ? quickActions.onAssign : undefined,
    onMoveTo: railShows(face.place, 'move') ? quickActions.onMoveTo : undefined,
    onToggleArchive: railShows(face.place, 'archive')
      ? quickActions.onToggleArchive
      : undefined,
    onOpenEntries: railShows(face.place, 'entries')
      ? quickActions.onOpenEntries
      : undefined,
    onAllocateToLane: face.place.allocator === 'off'
      ? undefined
      : quickActions.onAllocateToLane,
    onAllocateToBoard: face.place.allocator === 'off'
      ? undefined
      : quickActions.onAllocateToBoard,
    onLoadBoards: face.place.allocator === 'off'
      ? undefined
      : quickActions.onLoadBoards,
    onLoadBoardLanes: face.place.allocator === 'off'
      ? undefined
      : quickActions.onLoadBoardLanes,
    onMoveToBoard: face.place.allocator === 'off'
      ? undefined
      : quickActions.onMoveToBoard,
    onComposeEmail:
      face.compose === 'rail' ? quickActions.onComposeEmail : undefined,
    onSetActionDate: face.fields.actionDate
      ? quickActions.onSetActionDate
      : undefined,
  }
}

export function applyCardFaceStrip(
  face: ResolvedCardFacePrefs,
  quickActions: KanbanCardQuickActionHandlers | undefined,
): {
  onSetColor?: KanbanCardQuickActionHandlers['onSetColor']
  onAttachLabel?: KanbanCardQuickActionHandlers['onAttachLabel']
  onDetachLabel?: KanbanCardQuickActionHandlers['onDetachLabel']
  onComposeEmail?: KanbanCardQuickActionHandlers['onComposeEmail']
  onOpenWorkbook?: KanbanCardQuickActionHandlers['onOpenWorkbook']
  onPrefetchWorkbook?: KanbanCardQuickActionHandlers['onPrefetchWorkbook']
  onOpenFlowchart?: KanbanCardQuickActionHandlers['onOpenFlowchart']
} {
  if (!quickActions || face.railMode === 'hidden') return {}
  const colorOn = face.strip.color === 'on'
  return {
    onSetColor: colorOn ? quickActions.onSetColor : undefined,
    onAttachLabel: colorOn ? quickActions.onAttachLabel : undefined,
    onDetachLabel: colorOn ? quickActions.onDetachLabel : undefined,
    onComposeEmail:
      face.compose === 'strip' ? quickActions.onComposeEmail : undefined,
    onOpenWorkbook:
      face.strip.workbook === 'on' && face.fields.workbook
        ? quickActions.onOpenWorkbook
        : undefined,
    onPrefetchWorkbook:
      face.strip.workbook === 'on' ? quickActions.onPrefetchWorkbook : undefined,
    onOpenFlowchart:
      face.strip.flowchart === 'on' ? quickActions.onOpenFlowchart : undefined,
  }
}

export function applyCardFaceExtras(face: ResolvedCardFacePrefs): {
  showThread: boolean
  showCapture: boolean
  showChecklistExpand: boolean
} {
  return {
    showThread: face.extras.thread === 'on',
    showCapture: face.extras.quickCapture === 'on',
    showChecklistExpand: face.extras.checklistExpand === 'on',
  }
}

export function applyCardFaceMenuHandlers<T extends Record<string, unknown>>(
  face: ResolvedCardFacePrefs,
  props: T,
): T {
  const next: Record<string, unknown> = { ...props }
  const hide = (id: Parameters<typeof menuShows>[1], key: string) => {
    if (!menuShows(face, id)) next[key] = undefined
  }
  if (!menuShows(face, 'templates') && 'templates' in next) {
    next.templates = []
  }
  hide('notebook', 'onConvertToNotebook')
  hide('actionDate', 'onSetActionDate')
  hide('followup', 'onSetFollowupDate')
  hide('snoozeMenu', 'onSnooze')
  hide('blockMenu', 'onBlock')
  hide('reply', 'onComposeEmail')
  hide('meeting', 'onScheduleMeeting')
  hide('subcard', 'onMakeSubCardOf')
  hide('promote', 'onPromoteToTopLevel')
  hide('copyBoard', 'onLinkToBoard')
  hide('duplicate', 'onDuplicate')
  hide('copyLink', 'onCopyLink')
  hide('pinToggle', 'onTogglePin')
  hide('checklistPanel', 'onToggleChecklist')
  hide('delete', 'onDelete')
  hide('openWorkbook', 'onOpenWorkbook')
  if (face.compose !== 'menu' && 'onComposeEmail' in next) {
    next.onComposeEmail = undefined
  }
  if (!menuShows(face, 'alsoShow') && 'onAllocateToLane' in next) {
    next.onAllocateToLane = undefined
  }
  if (
    (face.place.due !== 'menu' || !face.fields.due) &&
    'onSetDueDate' in next
  ) {
    next.onSetDueDate = undefined
  }
  return next as T
}
